import { Box, Button, Delete, Edit, IconButton } from "@bcc-ui/ui-components";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { useAbortControllers } from "components/file-sharing/useAbortControllers";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import Modal from "components/modal/Modal";
import { AUTH_SIGNATURE_KEY_DELETE, AUTH_SIGNATURE_KEY_EDIT } from "domain/authority";
import { ReportSignatureKeyResult } from "domain/reportSignatureKeys";
import { reportSignatureKeyService } from "services/report/erasure/ReportSignatureKeyService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { userSessionService } from "services/user/UserSessionService";
import form from "styles/form.scss";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

interface Props {
    name: string;
    onEdit: () => void;
    onKeyChange: () => void;
    showResult: (result: ReportSignatureKeyResult, succeeded: boolean) => void;
}

const ReportSignatureKeyRowActions = (props: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [deleteDialogVisible, setDeleteDialogVisible] = React.useState(false);
    const [deleting, setDeleting] = React.useState(false);
    const abortControllers = useAbortControllers();

    const closeDeleteDialog = () => {
        abortControllers.abortAll();
        setDeleteDialogVisible(false);
    };

    const deleteKey = () => {
        const abortController = new AbortController();
        abortControllers.push(abortController);
        setDeleting(true);
        usageStatisticsService.sendEvent({
            category: Category.REPORT_SIGNATURE_KEY,
            action: Action.REMOVE_REPORT_SIGNATURE_KEY,
        });
        reportSignatureKeyService
            .deleteKey(props.name, abortController)
            .then(() => {
                setDeleteDialogVisible(false);
                props.showResult(
                    {
                        title: t("ReportSignatureKeys.deleteDialog.success.title"),
                        message: t("ReportSignatureKeys.deleteDialog.success.message", { name: props.name }),
                    },
                    true
                );
                props.onKeyChange();
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setDeleteDialogVisible(false);
                    props.showResult(
                        {
                            title: t("ReportSignatureKeys.deleteDialog.failed.title"),
                            message: t("ReportSignatureKeys.deleteDialog.failed.message"),
                        },
                        false
                    );
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setDeleting(false);
                }
            });
    };

    const canEdit = userSessionService.userHasAllAuthorities([AUTH_SIGNATURE_KEY_EDIT]);
    const canDelete = userSessionService.userHasAllAuthorities([AUTH_SIGNATURE_KEY_DELETE]);

    if (!canEdit && !canDelete) {
        return null;
    }

    return (
        <Box gap={"Xs"}>
            {canEdit && (
                <IconButton
                    onClick={props.onEdit}
                    title={t("ReportSignatureKeys.table.tooltipRename")}
                    testId={testIds.navigation.settingsDialog.reportSignatureKeys.table.renameButton}
                >
                    <Edit color={theme.iconFillColor} />
                </IconButton>
            )}
            {canDelete && (
                <IconButton
                    deleteButton
                    onClick={() => setDeleteDialogVisible(true)}
                    title={t("ReportSignatureKeys.table.tooltipDelete")}
                    testId={testIds.navigation.settingsDialog.reportSignatureKeys.table.deleteButton}
                >
                    <Delete color={theme.errorIconColor} />
                </IconButton>
            )}
            <Modal
                isOpen={deleteDialogVisible}
                hideModal={closeDeleteDialog}
                modalTitle={t("ReportSignatureKeys.deleteDialog.title")}
            >
                {deleting ? (
                    <LoadingIndicator />
                ) : (
                    <>
                        <Box className={form.deleteContainer}>
                            {t("ReportSignatureKeys.deleteDialog.introductionMessage", { name: props.name })}
                        </Box>
                        <Box gap={"Sm"} horizontalAlignment={"Right"}>
                            <Button
                                variant={"Secondary"}
                                onClick={closeDeleteDialog}
                                testId={testIds.common.dialog.closeButton}
                            >
                                {t("Common.cancel")}
                            </Button>
                            <Button
                                variant={"Delete"}
                                onClick={deleteKey}
                                testId={testIds.common.confirmationDialog.confirmButton}
                            >
                                {t("Common.delete")}
                            </Button>
                        </Box>
                    </>
                )}
            </Modal>
        </Box>
    );
};

export default ReportSignatureKeyRowActions;
